import { useSlotId } from '@react-aria/utils';
import { forwardRef, useContext } from 'react';
import type { CheckboxProps as AriaCheckboxProps } from 'react-aria-components';
import {
  Checkbox as AriaCheckbox,
  composeRenderProps,
  DEFAULT_SLOT,
  Provider,
  TextContext as AriaTextContext,
} from 'react-aria-components';

import type { SomeOptional } from '@infinex-private/ts-utils/types';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';
import { joinIds } from '@infinex/ui-utils/a11y';
import { isReactText } from '@infinex/ui-utils/react';

import type { CheckboxGroupContextValue } from './CheckboxGroup';
import { CheckboxGroupContext } from './CheckboxGroup';
import type { CheckboxPrimitiveProps } from './CheckboxPrimitive';
import { CheckboxPrimitive } from './CheckboxPrimitive';

import { CenterBaseline, Text, TextContext } from '../content';

export type CheckboxProps = SomeOptional<CheckboxPrimitiveProps, 'prominence'> &
  Omit<
    AriaCheckboxProps,
    'className' | 'style' | 'isInvalid' | 'validationBehavior'
  > &
  CheckboxGroupContextValue &
  BaseStyleProps &
  UnsafeStyleProps;

/**
 * A checkbox allows a user to select multiple options from a list of items, or
 * to toggle a boolean value when used as a discrete form control.
 *
 * [Taurus docs: `Checkbox`](https://ui-sb.app.infinex.dev/?path=/docs/components-checkbox--docs)
 */
export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  function Checkbox(props, labelRef) {
    const ctx = useContext(CheckboxGroupContext);
    const {
      'aria-describedby': describedByProp,
      prominence = ctx?.prominence,
      ...otherProps
    } = props;

    const { className, style } = useStyleProps(props);
    const descriptionSlotId = useSlotId([props.children]);

    return (
      <AriaCheckbox
        {...otherProps}
        ref={labelRef}
        aria-describedby={joinIds(describedByProp, descriptionSlotId)}
        className={cn(
          'group',
          'text-default flex items-baseline gap-2 text-sm font-medium',
          className
        )}
        style={style}
      >
        {composeRenderProps(props.children, (children, renderProps) => (
          <Provider
            values={[
              // clear "description" and "errorMessage" slots
              [AriaTextContext, {}],
              [
                TextContext,
                {
                  slots: {
                    // typically all typographic styles would be applied here,
                    // but we need the line-height to be declared on the wrapper
                    // for the baseline alignment
                    [DEFAULT_SLOT]: {
                      className: cn(
                        'group-hover:text-emphasis transition-colors',
                        {
                          'text-disabled': renderProps.isDisabled,
                        }
                      ),
                    },
                    description: {
                      // hide from screen readers since it's inside the label
                      // element. applied by "aria-describedby" to the input
                      'aria-hidden': true,
                      id: descriptionSlotId,
                      className: cn(
                        'text-secondary group-hover:text-default text-xs font-normal transition-colors',
                        {
                          'text-disabled': renderProps.isDisabled,
                        }
                      ),
                    },
                  },
                },
              ],
            ]}
          >
            <CenterBaseline className="relative">
              <CheckboxPrimitive prominence={prominence} {...renderProps} />
              <span className="absolute -inset-2" />
            </CenterBaseline>
            {isReactText(children) ? (
              <Text>{children}</Text>
            ) : (
              <div className="grid">{children}</div>
            )}
          </Provider>
        ))}
      </AriaCheckbox>
    );
  }
);
